/**
 * API client for external PricingSaaS API
 * Handles authentication, request construction, and error handling
 */

import type {
  FetchDiffsParams,
  ExternalDiffsApiResponse,
  ExternalDiffResponse,
  ApiError,
} from '@/types/externalDiffs';

// API configuration - Use Management API
const API_BASE_URL = import.meta.env.VITE_PRICINGSAAS_MANAGEMENT_API_URL ||
                     'https://nyqqynbtcrqdmhezmhsp.supabase.co/functions/v1/management';
const API_KEY = import.meta.env.VITE_PRICINGSAAS_MANAGEMENT_API_KEY || '';
const USE_MOCK = import.meta.env.VITE_USE_MOCK_EXTERNAL_API === 'true';

// Task queue API (separate Supabase project: qulnbyjrczvoxemtrili)
const TASK_QUEUE_BASE_URL = import.meta.env.VITE_TASK_QUEUE_API_URL ||
                            'https://qulnbyjrczvoxemtrili.supabase.co/functions/v1/management';
const TASK_QUEUE_API_KEY = import.meta.env.VITE_TASK_QUEUE_API_KEY || '';

// Retry configuration
const MAX_RETRIES = 3;
const INITIAL_RETRY_DELAY = 1000; // 1 second
const MAX_RETRY_DELAY = 10000; // 10 seconds

interface TierStatsBase {
  processed_this_week: number;
  outstanding_count: number;
  total_active_pages: number;
}

interface TierDiffStats {
  complete_count: number;
  incomplete_count: number;
  no_scrapes_count: number;
  total_active_pages: number;
  published_count: number;
  qa_checked_count: number;
  qa_pending_count: number;
  no_events_count: number;
}

/**
 * Build the API URL with query parameters for Management API
 */
function buildApiUrl(params: FetchDiffsParams): string {
  const { slug, period, limit = 100, offset = 0, regenerateAnnotations = false, event_types } = params;

  if (!slug) {
    throw new Error('Company slug is required for API endpoint');
  }

  // Management API endpoint for company-specific diffs
  // If period is provided, use path-based endpoint: /companies/{slug}/diffs/{period}
  const endpoint = period
    ? `${API_BASE_URL}/companies/${slug}/diffs/${period}`
    : `${API_BASE_URL}/companies/${slug}/diffs`;

  const queryParams = new URLSearchParams();

  queryParams.append('limit', limit.toString());
  queryParams.append('offset', offset.toString());

  // Add event_types filter if provided
  if (event_types && event_types.length > 0) {
    queryParams.append('event_types', event_types.join(','));
  }

  // Cache invalidation parameter
  if (regenerateAnnotations) {
    queryParams.append('regenerate_annotations', 'true');
  }

  // Add timestamp for cache busting
  queryParams.append('_t', Date.now().toString());

  const queryString = queryParams.toString();
  return queryString ? `${endpoint}?${queryString}` : endpoint;
}

/**
 * Get authentication headers for Management API
 */
function getAuthHeaders(): HeadersInit {
  return {
    'x-api-key': API_KEY,
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  };
}

/**
 * Handle API errors with detailed messages
 */
async function handleApiError(response: Response): Promise<never> {
  let errorMessage = `API request failed with status ${response.status}`;
  let errorDetails: any = null;

  try {
    const errorData = await response.json();
    errorMessage = errorData.message || errorMessage;
    errorDetails = errorData.details || null;
  } catch {
    // If error response is not JSON, use status text
    errorMessage = response.statusText || errorMessage;
  }

  const error: ApiError = {
    status: response.status,
    message: errorMessage,
    code: response.status === 401 ? 'UNAUTHORIZED' :
          response.status === 403 ? 'FORBIDDEN' :
          response.status === 429 ? 'RATE_LIMIT' :
          response.status >= 500 ? 'SERVER_ERROR' : 'API_ERROR',
    details: errorDetails,
  };

  throw error;
}

/**
 * Exponential backoff delay calculation
 */
function getRetryDelay(attempt: number): number {
  const delay = Math.min(INITIAL_RETRY_DELAY * Math.pow(2, attempt), MAX_RETRY_DELAY);
  // Add jitter to prevent thundering herd
  return delay + Math.random() * 1000;
}

/**
 * Sleep utility for retry delays
 */
function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Determine if error is retryable
 */
function isRetryableError(error: ApiError): boolean {
  // Don't retry auth errors or client errors (except rate limit)
  if (error.status === 401 || error.status === 403) {
    return false;
  }

  // Retry rate limits and server errors
  if (error.status === 429 || error.status >= 500) {
    return true;
  }

  return false;
}

/**
 * Fetch with retry logic
 */
async function fetchWithRetry<T>(
  url: string,
  options: RequestInit,
  attempt: number = 0
): Promise<T> {
  try {
    const response = await fetch(url, options);

    if (!response.ok) {
      await handleApiError(response);
    }

    return await response.json();
  } catch (error) {
    const apiError = error as ApiError;

    // If we haven't exceeded max retries and error is retryable
    if (attempt < MAX_RETRIES && isRetryableError(apiError)) {
      const delay = getRetryDelay(attempt);
      console.log(`Retrying request after ${delay}ms (attempt ${attempt + 1}/${MAX_RETRIES})`);
      await sleep(delay);
      return fetchWithRetry<T>(url, options, attempt + 1);
    }

    // Otherwise, throw the error
    throw error;
  }
}

/**
 * Main function to fetch diffs from external API
 */
export async function fetchDiffs(params: FetchDiffsParams = {}): Promise<ExternalDiffsApiResponse> {
  // Validate API key
  if (!USE_MOCK && !API_KEY) {
    throw new Error('VITE_PRICINGSAAS_API_KEY environment variable is not set');
  }


  if (USE_MOCK) {
    return fetchMockData(params);
  }

  // If specific slug is provided, fetch only that company
  if (params.slug) {
    const url = buildApiUrl(params);

    try {
      const response = await fetchWithRetry<
        // When period is in path, API returns a single diff object
        // When no period, API returns { diffs: [...], count, limit, offset }
        ExternalDiffResponse | {
          diffs: ExternalDiffResponse[];
          count: number;
          limit: number;
          offset: number;
        }
      >(url, {
        method: 'GET',
        headers: getAuthHeaders(),
      });

      // Handle single diff response (when period is in the URL path)
      if (params.period && !('diffs' in response)) {
        const singleDiff = response as ExternalDiffResponse;
        return {
          data: [singleDiff],
          totalCount: 1,
          requestsMade: 1,
        };
      }

      // Handle array response
      const arrayResponse = response as { diffs: ExternalDiffResponse[]; count: number };
      return {
        data: arrayResponse.diffs,
        totalCount: arrayResponse.count || arrayResponse.diffs.length,
        requestsMade: 1,
      };
    } catch (error) {
      console.error('[ExternalDiffsAPI] Error fetching diffs:', error);
      throw error;
    }
  }

  // If no slug provided, fetch all diffs using Management API period endpoints
  try {
    // Determine the period type and build appropriate endpoint
    let endpoint: string;
    if (params.period) {
      // Parse period type from format (e.g., "2025Q4" -> "quarters", "2025W01" -> "weeks")
      const periodType = params.period.includes('Q') ? 'quarters' :
                        params.period.includes('W') ? 'weeks' :
                        params.period.includes('M') ? 'months' :
                        params.period.includes('D') ? 'days' :
                        params.period.includes('Y') ? 'years' : 'quarters';

      // Use period-specific endpoint: /diffs/{type}/{period}
      endpoint = `${API_BASE_URL}/diffs/${periodType}/${params.period}`;
    } else {
      // Default to fetching all quarters if no period specified
      endpoint = `${API_BASE_URL}/diffs/quarters`;
    }

    const requestBody: any = {
      limit: params.limit || 100,
      offset: params.offset || 0,
    };

    // Add event_types filter if specified (top-level per API spec)
    if (params.event_types && params.event_types.length > 0) {
      requestBody.event_types = params.event_types;
    }

    // Add published filter if specified
    if (params.published !== undefined) {
      requestBody.published = params.published;
    } else {
      // No published filter = "All" status — include unpublished so QA-pending /
      // QA-checked diffs that still need to be processed are visible.
      requestBody.include_unpublished = true;
    }

    // Add QA status filter if specified
    if (params.qa_status) {
      requestBody.qa_status = params.qa_status;
    }

    // Add sort params if specified
    if (params.sort_by) {
      requestBody.sort_by = params.sort_by;
    }
    if (params.sort_order) {
      requestBody.sort_order = params.sort_order;
    }

    // Add regenerate_annotations parameter if specified
    if (params.regenerateAnnotations) {
      requestBody.regenerate_annotations = true;
    }

    // Determine HTTP method based on endpoint
    const method = params.period ? 'POST' : 'GET';
    const fetchOptions: RequestInit = {
      method,
      headers: getAuthHeaders(),
    };

    // Only add body for POST requests
    if (method === 'POST') {
      fetchOptions.body = JSON.stringify(requestBody);
    }

    const response = await fetchWithRetry<{
      diffs: ExternalDiffResponse[];
      count: number;
    }>(endpoint, fetchOptions);

    const diffs = response.diffs;

    return {
      data: diffs,
      totalCount: response.count || diffs.length,
      requestsMade: 1,
    };
  } catch (error) {
    console.error('[ExternalDiffsAPI] Error fetching diffs:', error);
    throw error;
  }
}

/**
 * Mock data for testing without API credits
 */
async function fetchMockData(params: FetchDiffsParams): Promise<ExternalDiffsApiResponse> {
  // Simulate network delay
  await sleep(500);

  const mockDiffs: ExternalDiffResponse[] = [
    {
      id: 'mock-diff-1',
      page_id: 'mock-page-1',
      slug: params.slug || 'notion',
      before_scrape_id: 'scrape-1',
      after_scrape_id: 'scrape-2',
      before_version: '20250101',
      after_version: '20250115',
      period: params.period || '2025Q1',
      before_period: '2025Q1',
      after_period: '2025Q1',
      title: 'Pricing Structure Update',
      description: 'Updated pricing tiers and features',
      event_count: 2,
      published: true,
      created_at: new Date().toISOString(),
      primary_original_url: 'https://res.cloudinary.com/demo/image/upload/sample.jpg',
      secondary_original_url: 'https://res.cloudinary.com/demo/image/upload/sample.jpg',
      company_logo: 'https://logo.clearbit.com/notion.so',
      events: [
        {
          id: 'event-1',
          type: 'pricing_change',
          category: 'pricing_structure',
          description: 'Changed pricing tier',
          slug: params.slug || 'notion',
          verified: false,
          annotations: {
            cloudinary_urls: {
              primary_original_url: 'https://res.cloudinary.com/demo/image/upload/sample.jpg',
              secondary_original_url: 'https://res.cloudinary.com/demo/image/upload/sample.jpg',
              primary_cropped_url: 'https://res.cloudinary.com/demo/image/upload/c_crop,w_800,h_600/sample.jpg',
              secondary_cropped_url: 'https://res.cloudinary.com/demo/image/upload/c_crop,w_800,h_600/sample.jpg',
            },
          },
        },
      ],
    },
  ];

  return {
    data: mockDiffs,
    totalCount: mockDiffs.length,
  };
}

/**
 * Fetch scrape coverage stats for the current week from the Management API.
 */
export async function fetchScrapeStats(): Promise<{
  processed_this_week: number;
  outstanding_count: number;
  total_active_pages: number;
  outstanding_slugs: string[];
  tier1?: TierStatsBase;
  tier2?: TierStatsBase;
}> {
  const response = await fetchWithRetry<{ summary: any; outstanding_slugs: string[]; tier1?: TierStatsBase; tier2?: TierStatsBase }>(
    `${API_BASE_URL}/stats/scrapes?tiers=true`,
    { method: 'GET', headers: getAuthHeaders() }
  );
  return { ...response.summary, outstanding_slugs: response.outstanding_slugs ?? [], tier1: response.tier1, tier2: response.tier2 };
}

/**
 * Fetch Tier 1 company slugs (companies on any non-archived watchlist).
 */
export async function fetchTier1Slugs(): Promise<string[]> {
  const response = await fetchWithRetry<{ slugs: string[] }>(
    `${API_BASE_URL}/watchlists/slugs`,
    { method: 'GET', headers: getAuthHeaders() }
  );
  return response.slugs ?? [];
}

/**
 * Fetch task queue health stats from the task queue management API.
 */
export async function fetchTaskQueueStats(): Promise<{
  total: number;
  by_status: { pending: number; completed: number; failed: number; archived: number };
  by_command: Record<string, number>;
  last_7_days: {
    total: number;
    by_status: { pending: number; completed: number; failed: number; archived: number };
    by_command: Record<string, number>;
  };
}> {
  return fetchWithRetry<any>(
    `${TASK_QUEUE_BASE_URL}/tasks/stats`,
    { method: 'GET', headers: { 'x-api-key': TASK_QUEUE_API_KEY, 'Content-Type': 'application/json', 'Accept': 'application/json' } }
  );
}

/**
 * Fetch diff stats for a period from the Management API stats endpoint.
 * Returns mutually exclusive counts: published, qa_checked, qa_pending, no_events.
 */
export async function fetchDiffStats(period: string): Promise<{
  complete_count: number;
  incomplete_count: number;
  no_scrapes_count: number;
  published_count: number;
  qa_checked_count: number;
  qa_pending_count: number;
  no_events_count: number;
  tier1?: TierDiffStats;
  tier2?: TierDiffStats;
  outstanding_slugs?: string[];
  complete_slugs?: string[];
  incomplete?: { slug: string; before_version?: string; after_version?: string }[];
  no_scrapes_slugs?: string[];
  published_slugs?: string[];
  qa_checked_slugs?: string[];
  qa_pending_slugs?: string[];
  no_events_slugs?: string[];
}> {
  const response = await fetchWithRetry<{
    summary: any;
    tier1?: TierDiffStats;
    tier2?: TierDiffStats;
    complete_slugs?: string[];
    incomplete?: { slug: string; before_version?: string; after_version?: string }[];
    no_scrapes_slugs?: string[];
    published_slugs?: string[];
    qa_checked_slugs?: string[];
    qa_pending_slugs?: string[];
    no_events_slugs?: string[];
  }>(
    `${API_BASE_URL}/stats/diffs/${period}?tiers=true`,
    { method: 'GET', headers: getAuthHeaders() }
  );
  return {
    ...response.summary,
    tier1: response.tier1,
    tier2: response.tier2,
    complete_slugs: response.complete_slugs ?? [],
    incomplete: response.incomplete ?? [],
    no_scrapes_slugs: response.no_scrapes_slugs ?? [],
    published_slugs: response.published_slugs ?? [],
    qa_checked_slugs: response.qa_checked_slugs ?? [],
    qa_pending_slugs: response.qa_pending_slugs ?? [],
    no_events_slugs: response.no_events_slugs ?? [],
  };
}

export interface DailyDiffDay {
  period: string;      // YYYYDnnn (UTC day-of-year, unpadded)
  date: string;        // YYYY-MM-DD (UTC)
  total: number;       // daily diffs that ran, incl. zero-event rows
  with_events: number; // diffs with ≥1 valid event
  published: number;   // published diffs
}

// Sibling edge fn on the same Supabase project as the management API
// (…/functions/v1/daily-diff-stats). Same X-API-Key model, read-only.
const DAILY_STATS_URL = `${API_BASE_URL.replace(/\/management\/?$/, '')}/daily-diff-stats`;

/**
 * Per-day counts for the Hot-100 daily lane, last `days` UTC days.
 * NOTE: /stats/diffs/{period} is version-window based and, for a daily period,
 * gets polluted by weekly-lane diffs (SOT diff-pipeline.md "Observability
 * gotchas"); /diffs/days/{period} never returns zero-event rows. The dedicated
 * fn reads app_diffs by exact daily period, so a day with total=0 really means
 * the daily lane did not run.
 */
export async function fetchDailyDiffStats(days = 14): Promise<DailyDiffDay[]> {
  const response = await fetchWithRetry<{ days: DailyDiffDay[] }>(
    `${DAILY_STATS_URL}?days=${days}`,
    { method: 'GET', headers: getAuthHeaders() }
  );
  return response.days ?? [];
}

/**
 * Warm up the Management API edge function to avoid cold-start delays.
 * Fires a lightweight GET to trigger the CORS preflight + function boot.
 * Safe to call multiple times; no-ops if already warm within TTL.
 */
let lastWarmupTime = 0;
const WARMUP_TTL = 4 * 60 * 1000; // 4 minutes

export interface AgentAction {
  id: string;
  created_at: string;
  agent: string;
  stage: string;
  action_type: string;
  slug: string | null;
  period: string | null;
  tier: number | null;
  reason: string | null;
  outcome: string | null;
  resolved: boolean;
  task_id: string | null;
  metadata: Record<string, unknown> | null;
}

export async function fetchAgentActions(params: {
  outcome?: string;
  resolved?: boolean;
  since?: string;
  limit?: number;
  slug?: string;
  action_type?: string | string[];
  stage?: string;
} = {}): Promise<{ data: AgentAction[]; count: number }> {
  if (!API_KEY) throw new Error('VITE_PRICINGSAAS_MANAGEMENT_API_KEY is not set');
  const url = new URL(`${API_BASE_URL}/agent-actions`);
  if (params.outcome) url.searchParams.set('outcome', params.outcome);
  if (params.resolved !== undefined) url.searchParams.set('resolved', String(params.resolved));
  if (params.since) url.searchParams.set('since', params.since);
  if (params.limit) url.searchParams.set('limit', String(params.limit));
  if (params.slug) url.searchParams.set('slug', params.slug);
  if (params.action_type) url.searchParams.set('action_type', Array.isArray(params.action_type) ? params.action_type.join(',') : params.action_type);
  if (params.stage) url.searchParams.set('stage', params.stage);
  const res = await fetch(url.toString(), { headers: getAuthHeaders() });
  if (!res.ok) await handleApiError(res);
  return res.json();
}

export interface TriageEvidence {
  label: string;
  url: string;
}

export interface TriageMetadata {
  bucket: 1 | 2 | 3;
  bucket_reason: string;
  cycle_id: string;
  pricing_url?: string;
  last_task_id?: string;
  last_error?: string;
  quality_score?: number;
  scraper_errors?: Record<string, string>;
  evidence: TriageEvidence[];
  retry_after?: string;
}

/** Fetch the latest triage action per outstanding slug (B1/B2/B3 classification). */
export async function fetchTriageActions(): Promise<Map<string, AgentAction & { metadata: TriageMetadata }>> {
  const res = await fetchAgentActions({
    stage: 'triage',
    since: '168h',
    limit: 2000,
  });
  const map = new Map<string, AgentAction & { metadata: TriageMetadata }>();
  for (const action of res.data) {
    if (action.slug && !map.has(action.slug)) {
      map.set(action.slug, action as AgentAction & { metadata: TriageMetadata });
    }
  }
  return map;
}

export interface CompanyDetails {
  slug: string;
  name: string;
  pages: Array<{ subslug: string; pricing_page_url: string }>;
}

export async function fetchCompanyDetails(slug: string): Promise<CompanyDetails> {
  if (!API_KEY) throw new Error('VITE_PRICINGSAAS_MANAGEMENT_API_KEY is not set');
  const res = await fetch(`${API_BASE_URL}/companies/${encodeURIComponent(slug)}`, { headers: getAuthHeaders() });
  if (!res.ok) await handleApiError(res);
  return res.json();
}

export async function archivePage(slug: string): Promise<void> {
  if (!API_KEY) throw new Error('VITE_PRICINGSAAS_MANAGEMENT_API_KEY is not set');
  const res = await fetch(`${API_BASE_URL}/pages/slug/${encodeURIComponent(slug)}`, {
    method: 'PATCH',
    headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify({ archived: true }),
  });
  if (!res.ok) await handleApiError(res);
}

export async function unarchivePage(slug: string): Promise<void> {
  if (!API_KEY) throw new Error('VITE_PRICINGSAAS_MANAGEMENT_API_KEY is not set');
  const res = await fetch(`${API_BASE_URL}/pages/slug/${encodeURIComponent(slug)}`, {
    method: 'PATCH',
    headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify({ archived: false }),
  });
  if (!res.ok) await handleApiError(res);
}

const TASK_QUEUE_BASE = import.meta.env.VITE_TASK_QUEUE_API_URL ?? '';
const TASK_QUEUE_KEY = import.meta.env.VITE_TASK_QUEUE_API_KEY ?? '';

export async function queueScrape(slug: string): Promise<{ id: string }> {
  if (!TASK_QUEUE_KEY) throw new Error('VITE_TASK_QUEUE_API_KEY is not set');
  const res = await fetch(`${TASK_QUEUE_BASE}/tasks`, {
    method: 'POST',
    headers: { 'X-API-Key': TASK_QUEUE_KEY, 'Content-Type': 'application/json' },
    body: JSON.stringify({ command: 'scrape', slug }),
  });
  if (!res.ok) await handleApiError(res);
  return res.json();
}

export async function updatePricingUrl(slug: string, url: string): Promise<void> {
  if (!API_KEY) throw new Error('VITE_PRICINGSAAS_MANAGEMENT_API_KEY is not set');
  const res = await fetch(`${API_BASE_URL}/pages/slug/${encodeURIComponent(slug)}`, {
    method: 'PATCH',
    headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify({ pricing_page_url: url }),
  });
  if (!res.ok) await handleApiError(res);
}

export function warmupManagementApi(): void {
  const now = Date.now();
  if (now - lastWarmupTime < WARMUP_TTL) return; // already warm
  lastWarmupTime = now;

  // Fire-and-forget — we don't need the result.
  // Match the URL used by useManagementPeriods so the edge function's response cache aligns.
  fetch(`${API_BASE_URL}/diffs?include_unpublished=true`, {
    method: 'GET',
    headers: getAuthHeaders(),
  }).catch(() => {
    // Silently ignore errors — this is a best-effort warmup
  });
}

/**
 * Get API configuration status
 */
export function getApiStatus(): {
  configured: boolean;
  useMock: boolean;
  baseUrl: string;
} {
  return {
    configured: !!API_KEY || USE_MOCK,
    useMock: USE_MOCK,
    baseUrl: API_BASE_URL,
  };
}

/**
 * Invalidate annotation cache by refetching with regenerate_annotations=true
 * This is a convenience wrapper around fetchDiffs with cache invalidation
 */
export async function invalidateAnnotationCache(
  slug: string,
  period?: string
): Promise<ExternalDiffsApiResponse> {
  console.log('🔄 [ExternalDiffsAPI] Invalidating annotation cache for:', { slug, period });

  return fetchDiffs({
    slug,
    period,
    regenerateAnnotations: true,
  });
}

/**
 * Update diff fields (title, description)
 * Uses PATCH /management/diffs/{diff_id} endpoint
 */
export async function updateDiffFields(
  diffId: string,
  fields: { title?: string; description?: string }
): Promise<void> {
  if (!API_KEY) {
    throw new Error('VITE_PRICINGSAAS_MANAGEMENT_API_KEY environment variable is not set');
  }

  const url = `${API_BASE_URL}/diffs/${diffId}`;

  try {
    const response = await fetch(url, {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify(fields),
    });

    if (!response.ok) {
      await handleApiError(response);
    }
  } catch (error) {
    console.error('[ExternalDiffsAPI] Error updating diff fields:', error);
    throw error;
  }
}

/**
 * Toggle highlight flag for a diff
 * Uses PATCH /management/diffs/{diff_id} endpoint
 */
export async function toggleDiffHighlight(
  diffId: string,
  highlight: boolean
): Promise<void> {
  if (!API_KEY) {
    throw new Error('VITE_PRICINGSAAS_MANAGEMENT_API_KEY environment variable is not set');
  }

  const url = `${API_BASE_URL}/diffs/${diffId}`;

  try {
    const response = await fetch(url, {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify({ highlight }),
    });

    if (!response.ok) {
      await handleApiError(response);
    }
  } catch (error) {
    console.error('[ExternalDiffsAPI] Error toggling highlight:', error);
    throw error;
  }
}

/**
 * Set published flag for a diff
 * Uses PATCH /management/diffs/{diff_id} endpoint
 */
export async function setDiffPublished(diffId: string, published: boolean): Promise<void> {
  if (!API_KEY) {
    throw new Error('VITE_PRICINGSAAS_MANAGEMENT_API_KEY environment variable is not set');
  }

  const url = `${API_BASE_URL}/diffs/${diffId}`;

  try {
    const response = await fetch(url, {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify({ published }),
    });

    if (!response.ok) {
      await handleApiError(response);
    }
  } catch (error) {
    console.error('[ExternalDiffsAPI] Error setting published status:', error);
    throw error;
  }
}

/**
 * Reset QA check status for a diff (send back to QA pending)
 * Uses PATCH /management/diffs/{diff_id} endpoint
 */
export async function resetDiffQACheck(diffId: string): Promise<void> {
  if (!API_KEY) {
    throw new Error('VITE_PRICINGSAAS_MANAGEMENT_API_KEY environment variable is not set');
  }

  const url = `${API_BASE_URL}/diffs/${diffId}`;

  try {
    const response = await fetch(url, {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify({ qa_check: null }),
    });

    if (!response.ok) {
      await handleApiError(response);
    }
  } catch (error) {
    console.error('[ExternalDiffsAPI] Error resetting QA check:', error);
    throw error;
  }
}


// ─── Extraction Coverage ─────────────────────────────────────────────────────

export interface ExtractionCoverageStats {
  extracted_count: number;
  fallback_count: number;
  failed_count: number;
  pending_count: number;
  total_active: number;
  watchlisted_extracted: number;
  watchlisted_total: number;
  method_breakdown: Record<string, number>;
  fallback_breakdown: Record<string, number>;
}

export async function fetchExtractionStats(): Promise<ExtractionCoverageStats> {
  const response = await fetchWithRetry<ExtractionCoverageStats>(
    `${API_BASE_URL}/stats/extractions`,
    { method: 'GET', headers: getAuthHeaders() }
  );
  return response;
}

export interface ExtractionSlugDetail {
  slug: string;
  version: string | null;
  method: string;
  status: string;
  fallback_method: string | null;
  fallback_reason: string | null;
  plan_count: number;
  feature_count: number;
  duration_seconds: number | null;
  task_id: string | null;
  created_at: string;
}

export async function fetchExtractionDetails(): Promise<ExtractionSlugDetail[]> {
  const response = await fetchWithRetry<{ slugs: ExtractionSlugDetail[]; count: number }>(
    `${API_BASE_URL}/stats/extractions/details`,
    { method: 'GET', headers: getAuthHeaders() }
  );
  return response.slugs ?? [];
}

// ─── Heartbeat Status ────────────────────────────────────────────────────────

export interface HeartbeatStatus {
  lastPingAt: string | null;
  ageSecs: number | null;
  status: 'healthy' | 'warning' | 'dead';
  reason: string;
  t1Outstanding: number | null;
  t2Outstanding: number | null;
  actionsThisCycle: number | null;
  triage: { b1: number; b2: number; b3: number; total: number } | null;
}

export async function fetchHeartbeatStatus(): Promise<HeartbeatStatus> {
  const url = `${API_BASE_URL}/agent-actions?action_type=heartbeat_ping&limit=1`;
  const res = await fetch(url, { headers: getAuthHeaders() });
  if (!res.ok) throw new Error(`Heartbeat fetch failed: ${res.status}`);
  const data = await res.json();
  const row = data?.data?.[0] ?? null;

  if (!row) {
    return { lastPingAt: null, ageSecs: null, status: 'dead', reason: 'No pings found', t1Outstanding: null, t2Outstanding: null, actionsThisCycle: null, triage: null };
  }

  const ageSecs = Math.floor((Date.now() - new Date(row.created_at).getTime()) / 1000);
  const status: HeartbeatStatus['status'] = ageSecs < 1200 ? 'healthy' : ageSecs < 3600 ? 'warning' : 'dead';
  const meta = row.metadata ?? {};

  return {
    lastPingAt: row.created_at,
    ageSecs,
    status,
    reason: row.reason ?? '',
    t1Outstanding: meta.t1_scrape_outstanding ?? null,
    t2Outstanding: meta.t2_outstanding ?? null,
    actionsThisCycle: meta.actions_this_cycle ?? null,
    triage: meta.triage
      ? { b1: meta.triage.b1_scheduled ?? 0, b2: meta.triage.b2_review ?? 0, b3: meta.triage.b3_escalated ?? 0, total: meta.triage.total_slugs ?? 0 }
      : null,
  };
}

// ─── Heartbeat ping history ────────────────────────────────────────────────

export interface HeartbeatPing {
  id: string;
  created_at: string;
  reason: string;
  outcome: string;
  metadata: {
    t1_scrape_outstanding?: number;
    t2_outstanding?: number;
    actions_this_cycle?: number;
    published_this_cycle?: number;
    t1_daily_diff_outstanding?: number;
    triage?: {
      b1_scheduled?: number;
      b2_review?: number;
      b3_escalated?: number;
      skipped_anti_loop?: number;
      cycle_id?: string;
      total_slugs?: number;
    };
  };
}

export async function fetchHeartbeatPings(limit = 10, offset = 0): Promise<{ data: HeartbeatPing[]; count: number }> {
  const url = `${API_BASE_URL}/agent-actions?action_type=heartbeat_ping&limit=${limit}&offset=${offset}`;
  const res = await fetch(url, { headers: getAuthHeaders() });
  if (!res.ok) throw new Error(`Heartbeat pings fetch failed: ${res.status}`);
  const data = await res.json();
  return { data: data.data ?? [], count: data.count ?? 0 };
}

// ─── Cycle detail actions ──────────────────────────────────────────────────

export interface CycleAction {
  id: string;
  created_at: string;
  action_type: string;
  slug: string | null;
  stage: string;
  reason: string;
  outcome: string;
  tier: number | null;
  metadata: Record<string, unknown>;
}

/**
 * Fetch all pipeline actions that occurred in a heartbeat cycle window.
 * Actions happen BEFORE the ping is logged, so:
 *   `since` = the prior (older) ping's created_at
 *   `until` = this ping's created_at (filter client-side — API may not support until=)
 */
export async function fetchCycleActions(since: string, until: string): Promise<CycleAction[]> {
  const url = `${API_BASE_URL}/agent-actions?since=${encodeURIComponent(since)}&limit=500`;
  const res = await fetch(url, { headers: getAuthHeaders() });
  if (!res.ok) throw new Error(`Cycle actions fetch failed: ${res.status}`);
  const data = await res.json();
  const untilMs = new Date(until).getTime();
  return (data.data ?? []).filter(
    (a: CycleAction) =>
      a.action_type !== 'heartbeat_ping' &&
      new Date(a.created_at).getTime() <= untilMs
  );
}
